import React, { useState, useRef, memo } from 'react';
import { useForm, FormContext } from "react-hook-form";

import API  from '@aws-amplify/api';
import Storage from '@aws-amplify/storage';

import ContactSection from '@components/Form/Sections/ContactSection';
import PeSection from '@components/Form/Sections/PeSection';
import SignatureSection from '@components/Form/Sections/SignatureSection';
import TncSection from '@components/Form/Sections/TncSection';

import Complete from '@components/Complete';
import Spinner from '@components/Spinner';

import { convertDistributorApiData, getHashedFilePath, getHashedBase64Path, convertCheckExistsApiData } from '@helpers';
import { awsConfig } from '@configs';
import { useTranslate } from '@hooks/translate';

const Form = () => {
    const methods = useForm({
        mode: 'onBlur', 
        reValidateMode: 'onBlur',
        defaultValues: {
            'country': 'KZ',
            'telephone_country_code': 7
        }
    });
    const {handleSubmit, setValue, getValues, watch, setError } = methods;
    const { pe_certificate_src, signature_src} = getValues();
    
    const translate = useTranslate();
    const signatureRef = useRef(null);

    const signatureToImage = (clear = false) => {
        if(clear) {
            signatureRef.current.clear();
        }
        if(!signatureRef.current.isEmpty()) {
            const trimmedCanvas = signatureRef.current.getTrimmedCanvas()
            const trimmedImage = trimmedCanvas.toDataURL('image/png');
            const image = signatureRef.current.toDataURL('image/png');
            const hashedPath = getHashedBase64Path(trimmedImage, 'SIGNATURE');

            setValue('signature_src', hashedPath);
            setTrimmedSignatureSrc(trimmedImage);
            setSignatureSrc(image);
        } else {
            setValue('signature_src', '');
            setTrimmedSignatureSrc('');
            setSignatureSrc('');
        }
    }

    const handleChangeFile = (file, key, func) => {
        if(file){
            const mapping = [
                {key: 'pe_certificate_src', type: 'PE_CERTIFICATE'}
            ]
            const type = mapping.find(item => item.key === key).type;
            func(file);
            const hashedPath = getHashedFilePath(file, type);
            setValue(key, hashedPath);
        }
    }

    const onSubmit = data => {
        setIsLoading(true);
        postCheckAvailability(data);
        // postRegisterPE(data);
    };

    const uploadSignature = async (base64, path) => {
        const base64Data = new Buffer.from(base64.replace(/^data:image\/\w+;base64,/, ""), 'base64');
        const type = base64.split(';')[0].split('/')[1];

        if(base64) {
            const { key } = await Storage.put(path, base64Data, {
                contentType: `image/${type}`,
                contentEncoding: 'base64',
            }).catch(err => console.log(err));
            return key;
        }
    }

    const submitUploadFile = async (file, path) => {;
        if(file) {
            const { key } = await Storage.put(path, file, {
                contentType: file.type,
            }).catch(err => console.log(err));
            return key;
        }
    }

    const [peCertSrc, setPeCertSrc] = useState('');
    const [signatureSrc, setSignatureSrc] = useState('');
    const [trimmedSignature, setTrimmedSignatureSrc] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // TODO: Check if distributor ID exists?
    const postCheckAvailability = async(data) => {
        let apiName = awsConfig?.API?.endpoints?.[0]?.name;
        let path = '/member/v1/checkPERegAvailability';
        let myInit = {
            headers: {},
            response: true,
        }
        myInit.body = convertCheckExistsApiData(data);
        return await API.post(apiName, path, myInit)
            .then(res => {
              console.log(JSON.stringify(res));
              if(res.data?.success) {
                postRegisterPE(data);
              } else {
                alert(translate("not eligible for PE registration"));
                setIsLoading(false);
              }
            })
            .catch(error => {
                alert(translate("not eligible for PE registration"))
                setIsLoading(false);
            });
    }

    const postRegisterPE= async(data) => {
        let apiName = awsConfig?.API?.endpoints?.[0].name;
        let path = '/member/v1/registerPE';
        let myInit = {
            headers: {},
            response: true,
        }
        myInit.body = convertDistributorApiData(data);

        return await API.post(apiName, path, myInit)
            .then(async res => {
                if(res.data?.success) {
                    if(trimmedSignature) {
                        await uploadSignature(trimmedSignature, signature_src);
                    }
                    if(peCertSrc) {
                        await submitUploadFile(peCertSrc, pe_certificate_src);
                    }
                    setIsLoading(false);
                    setIsCompleted(true);
                    if(typeof window !== 'undefined') {
                        window.dataLayer.push({'event': 'isSuccess'});
                    }
                } else {
                    setIsLoading(false);
                }
            })
            .catch(error => {
                setIsLoading(false);
                alert(error.response?.data?.error?.message);
            });
    }

    if(isCompleted) {
        return (
            <Complete />
        )
    }

    return (
        <div className="container">
            <FormContext {...methods} > 
                <form id="form-reg-contact" method="POST" onSubmit={handleSubmit(onSubmit)}>

                    <div id="wrapper-reg-form-view">
                        <div className="page-title">
                            <h5>{translate('Kazakhstan Distributor Registration')}</h5>
                        </div>
                        <ContactSection 
                            handleChangeFile={handleChangeFile}
                        />
                        
                        <PeSection 
                            handleChangeFile={handleChangeFile}
                            setPeCertSrc={setPeCertSrc}
                            peCertSrc={peCertSrc}
                        />

                        <SignatureSection 
                            signatureToImage={signatureToImage}
                            signatureSrc={signatureSrc}
                            ref={signatureRef}
                        />
                    </div>
                    {/* T&C */}
                    <TncSection />

                    {/* Submit */}
                    <div className="action-group">
                        <button id="btn-reg-review-info" type="submit" title="Submit" className="btn btn-theme">{translate('Submit')}</button>
                    </div>
                </form>
            </FormContext>
            {isLoading && <Spinner/>}
        </div>
    )
}

export default memo(Form);